import {MrcEntryCreateRequest, MrcEntryResponse} from 'common/types/store/mrcTypes';
import dayjs from 'dayjs';

/**
 * Calculates the percentage of the signing process completed.
 * 25% if only sms_sent_at is set,
 * 50% if mrc_sent_at is set,
 * 100% if mrc_signed_at or mrc_rejected_at is set.
 */
export function getSigningProgressPercent(entry: MrcEntryResponse): string | number {
	if (entry.mrc_signed_at || entry.mrc_rejected_at) {
		return '100 %';
	}
	if (entry.mrc_sent_at) {
		return '50 %';
	}
	if (entry.sms_sent_at) {
		return '25 %';
	}
	return '0 %';
}

/**
 * Calculates the average time to sign and returns a formatted string.
 * Example: "2.6 h", "45 m", "1.2 d"
 * Returns '-' if either sms_sent_at or mrc_signed_at is missing.
 */
export function getAvgTimeToSign(entry: MrcEntryResponse): string {
	if (!entry.sms_sent_at || !entry.mrc_signed_at) return '-';
	const start = dayjs(entry.sms_sent_at);
	const end = dayjs(entry.mrc_signed_at);
	const diffMs = end.diff(start);

	if (diffMs < 60 * 60 * 1000) {
		// Less than 1 hour: show minutes
		const mins = diffMs / (60 * 1000);
		return `${mins.toFixed(0)} m`;
	} else if (diffMs < 24 * 60 * 60 * 1000) {
		// Less than 1 day: show hours (with one decimal)
		const hours = diffMs / (60 * 60 * 1000);
		return `${hours.toFixed(1)} h`;
	} else {
		// 1 day or more: show days (with one decimal)
		const days = diffMs / (24 * 60 * 60 * 1000);
		return `${days.toFixed(1)} d`;
	}
}

export function isSignedStatus(entry: MrcEntryResponse): boolean {
	return Boolean(entry.mrc_signed_at && !entry.mrc_rejected_at);
}

/**
 * Converts dd.mm.yyyy date string to ISO format
 */
const convertToISO = (dateStr: string): string => {
	const [day, month, year] = dateStr.split('.');
	const date = new Date(Number(year), Number(month) - 1, Number(day));
	return date.toISOString();
};

/**
 * Transforms form data into the format required by the MRC API
 * - Filters out null/undefined/empty values
 * - Converts birth dates from dd.mm.yyyy to ISO format
 * - Converts VAT numbers from strings to integers
 */
export const toMrcApiFormat = (
	formData: Partial<MrcEntryCreateRequest>,
	templateId: number | string
): MrcEntryCreateRequest => {
	const filteredData = Object.entries(formData).reduce((acc, [key, value]) => {
		if (value !== null && value !== undefined && value !== '') {
			// Convert birth dates to ISO format
			if ((key === 'from_birth_date' || key === 'to_birth_date') && typeof value === 'string') {
				return {
					...acc,
					[key]: convertToISO(value),
				};
			}
			// Convert VAT numbers to integers
			if ((key === 'from_vat_no' || key === 'to_vat_no') && typeof value === 'string') {
				return {
					...acc,
					[key]: Number(value),
				};
			}
			return {
				...acc,
				[key]: value,
			};
		}
		return acc;
	}, {} as Record<string, unknown>);

	return {
		oneflow_template_id: Number(templateId),
		phone_number: formData.phone_number || '',
		...filteredData,
	} as MrcEntryCreateRequest;
};
